import React, { useRef, useState } from "react";
import { Upload, X, ChevronLeft, ChevronRight, Star, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { uploadFile, mediaUrl, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

// Multi-photo gallery manager: upload many at once, reorder, remove, set as main
export default function GalleryInput({ value = [], onChange, onSetMain, testid = "gallery" }) {
  const ref = useRef(null);
  const [pending, setPending] = useState([]); // {id, preview, progress}
  const [url, setUrl] = useState("");
  const listRef = useRef(value);
  listRef.current = value;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (ref.current) ref.current.value = "";
    if (!files.length) return;
    const jobs = files.map((f) => ({ f, id: `${Date.now()}-${Math.random()}`, preview: URL.createObjectURL(f) }));
    setPending((p) => [...p, ...jobs.map((j) => ({ id: j.id, preview: j.preview, progress: 0 }))]);
    let ok = 0;
    const queue = [...jobs];
    const worker = async () => {
      while (queue.length) {
        const j = queue.shift();
        try {
          if (j.f.size > 20 * 1024 * 1024) throw new Error("big");
          const u = await uploadFile(j.f, (pr) => setPending((p) => p.map((x) => (x.id === j.id ? { ...x, progress: pr } : x))));
          onChange([...listRef.current, u]);
          listRef.current = [...listRef.current, u];
          ok += 1;
        } catch (err) {
          toast.error(err.message === "big" ? `${j.f.name} supera 20MB` : errMsg(err, `No se pudo subir ${j.f.name}`));
        } finally {
          setPending((p) => p.filter((x) => x.id !== j.id));
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    if (ok) toast.success(`${ok} foto${ok > 1 ? "s" : ""} agregada${ok > 1 ? "s" : ""}`);
  };

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const arr = [...value];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Galería de fotos <span className="text-gray-400 font-normal">({value.length})</span></label>
        <button type="button" onClick={() => ref.current?.click()} data-testid={`${testid}-upload-btn`}
          className="btn-brand h-9 px-3 rounded-md text-sm font-medium flex items-center gap-1.5">
          <Upload className="w-4 h-4" />Subir fotos
        </button>
        <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid={`${testid}-file-input`} />
      </div>
      <p className="text-xs text-gray-500">Puedes seleccionar muchas fotos a la vez (sin límite, máx. 20MB cada una). Usa las flechas para ordenar y la estrella para ponerla como principal.</p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {value.map((g, i) => (
          <div key={`${g}-${i}`} className="group relative aspect-[4/3] rounded-md overflow-hidden border border-gray-200 bg-gray-100" data-testid={`${testid}-item`}>
            <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" loading="lazy" />
            <span className="absolute top-1 left-1 text-[10px] font-semibold bg-black/60 text-white rounded px-1.5">{i + 1}</span>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Quitar"
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"><X className="w-3.5 h-3.5" /></button>
            <div className="absolute bottom-0 inset-x-0 flex justify-between p-1 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center" aria-label="Mover a la izquierda"><ChevronLeft className="w-4 h-4" /></button>
              {onSetMain && <button type="button" onClick={() => onSetMain(g, i)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center" title="Poner como principal"><Star className="w-3.5 h-3.5 text-amber-500" /></button>}
              <button type="button" onClick={() => move(i, 1)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center" aria-label="Mover a la derecha"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {pending.map((p) => (
          <div key={p.id} className="relative aspect-[4/3] rounded-md overflow-hidden border border-gray-200">
            <img src={p.preview} alt="" className="w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white text-xs font-semibold gap-1">
              <Loader2 className="w-4 h-4 animate-spin" />{p.progress}%
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="O pega la URL de una imagen" />
        <button type="button" onClick={() => { if (url.trim()) { onChange([...value, url.trim()]); setUrl(""); } }}
          className="shrink-0 h-10 px-3 rounded-md border border-gray-300 hover:border-[var(--brand)] text-sm flex items-center gap-1.5"><Link2 className="w-4 h-4" />Agregar</button>
      </div>
    </div>
  );
}
